import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../lib/setupAxiosAuth';
import { useCrawlFormState } from './useCrawlFormState';
import { useRecursiveCrawlStream } from './useRecursiveCrawlStream';
import { pollCrawlJobUntilDone } from './useCrawlJobPolling';

const API = '/api';

function validateCrawlUrl(url) {
  if (!url.trim()) {
    return 'Veuillez entrer une URL';
  }
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return "L'URL doit commencer par http:// ou https://";
  }
  return null;
}

function parseUrlList(urlList) {
  return urlList.split('\n').map((line) => line.trim()).filter((line) => line && (line.startsWith('http://') || line.startsWith('https://')));
}

function validateBatchUrls(urlList) {
  const urls = parseUrlList(urlList);
  if (urls.length === 0) {
    return 'Veuillez entrer au moins une URL valide (une par ligne, commençant par http:// ou https://)';
  }
  return null;
}

function validateAuthConfig(crawlState) {
  if (crawlState.authMode === 'google' && !crawlState.googleEmail.trim()) {
    return 'Veuillez saisir l’email Google à utiliser pour le crawl';
  }
  if (
    crawlState.authMode === 'google' &&
    !crawlState.useSavedGoogleSession &&
    !crawlState.googlePassword.trim()
  ) {
    return 'Veuillez saisir le mot de passe Google ou activer la réutilisation de session enregistrée';
  }
  if (
    crawlState.authMode === 'credentials' &&
    Boolean(crawlState.username.trim()) !== Boolean(crawlState.password.trim())
  ) {
    return 'Veuillez renseigner à la fois l’identifiant et le mot de passe';
  }
  if (crawlState.authMode === 'cookies' && !crawlState.sessionCookies.trim()) {
    return 'Veuillez fournir les cookies de session';
  }
  return null;
}

function getCrawlErrorMessage(error) {
  return error.response?.data?.detail || error.message || 'Erreur lors du crawling';
}

const AUTH_FIELD_MAP = {
  credentials: (c) => ({ username: c.username || null, password: c.password || null }),
  google: (c) => ({
    google_email: c.googleEmail || null,
    google_password: c.googlePassword || null,
    remember_google_session: c.rememberGoogleSession,
    use_saved_google_session: c.useSavedGoogleSession,
  }),
  cookies: () => ({}),
};

function buildAuthPayload(config) {
  const modeFields = (AUTH_FIELD_MAP[config.authMode] || AUTH_FIELD_MAP.cookies)(config);
  return {
    auth_mode: config.authMode,
    session_cookies: config.sessionCookies || null,
    username: null,
    password: null,
    google_email: null,
    google_password: null,
    remember_google_session: false,
    use_saved_google_session: false,
    ...modeFields,
  };
}

function buildSimpleCrawlPayload(simpleCrawlConfig) {
  return {
    knowledge_base_id: simpleCrawlConfig.knowledgeBaseId,
    url: simpleCrawlConfig.url,
    force_recrawl: simpleCrawlConfig.forceRecrawl || false,
    ...buildAuthPayload(simpleCrawlConfig),
  };
}

function buildBatchCrawlPayload(batchConfig) {
  return {
    knowledge_base_id: batchConfig.knowledgeBaseId,
    urls: parseUrlList(batchConfig.urlList),
    force_recrawl: batchConfig.forceRecrawl || false,
    ...buildAuthPayload(batchConfig),
  };
}

function handleSimpleCrawlStreamEvent(data, crawlStateHandlers, jobIdRef) {
  if (data.type === 'start') {
    jobIdRef.current = data.job_id || data.crawl_id;
    return null;
  }

  if (data.type === 'heartbeat') {
    return null;
  }

  if (data.type === 'progress') {
    crawlStateHandlers.setProgress((current) => ({
      ...current,
      ...data,
      status: data.status || current?.status || 'En cours...',
      images_total: data.images_total ?? current?.images_total ?? 0,
      images_done: data.images_done ?? current?.images_done ?? 0,
      url_index: data.url_index ?? current?.url_index ?? 0,
      url_total: data.url_total ?? current?.url_total ?? 0,
      indexed: data.indexed ?? current?.indexed ?? 0,
      duplicates: data.duplicates ?? current?.duplicates ?? 0,
      errors: data.errors ?? current?.errors ?? 0,
      current_url: data.current_url ?? current?.current_url ?? '',
    }));
    return null;
  }

  if (data.type === 'complete') {
    crawlStateHandlers.setCompletedResult(data);
    crawlStateHandlers.setCrawling(false);
    return true;
  }

  if (data.type === 'error') {
    throw new Error(data.message || 'Erreur lors du crawling');
  }

  return null;
}

async function readSimpleCrawlStream(reader, crawlStateHandlers, jobIdRef) {
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (value) {
      buffer += decoder.decode(value, { stream: !done });
    }

    const events = buffer.split('\n\n');
    buffer = done ? '' : (events.pop() || '');

    for (const eventChunk of events) {
      const dataLine = eventChunk.split('\n').find((line) => line.startsWith('data: '));
      if (!dataLine) {
        continue;
      }

      const data = JSON.parse(dataLine.slice(6));
      const result = handleSimpleCrawlStreamEvent(data, crawlStateHandlers, jobIdRef);
      if (result !== null) {
        return result;
      }
    }

    if (done) {
      return false;
    }
  }
}

async function runStreamingCrawl(endpoint, payload, crawlStateHandlers) {
  const response = await fetch(`${API}${endpoint}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok || !response.body) {
    throw new Error('Erreur lors du démarrage du crawl');
  }

  const jobIdRef = { current: null };
  const completed = await readSimpleCrawlStream(response.body.getReader(), crawlStateHandlers, jobIdRef);

  if (completed) {
    return true;
  }
  if (jobIdRef.current) {
    toast.info('Connexion interrompue — reprise du suivi en arrière-plan...');
    return await pollCrawlJobUntilDone(jobIdRef.current, crawlStateHandlers);
  }
  return false;
}

function resetCrawlState(crawlStateHandlers) {
  crawlStateHandlers.setCrawling(true);
  crawlStateHandlers.setProgress(null);
  crawlStateHandlers.setCompletedResult(null);
}

function finalizeCrawlState(crawlCompleted, crawlStateHandlers) {
  if (!crawlCompleted) {
    crawlStateHandlers.setCompletedResult({
      success: true,
      disconnected: true,
      message: 'Connexion interrompue — le traitement continue en arrière-plan. Actualisez la liste des documents plus tard.',
      pages_crawled: 0,
      documents_created: 0,
      images_analyzed: 0,
    });
    crawlStateHandlers.setCrawling(false);
  }
}

function useCrawlCloseHandler(completedResult, onClose, onSuccess) {
  return useCallback(() => {
    if (completedResult) {
      onSuccess();
    }
    onClose();
  }, [completedResult, onClose, onSuccess]);
}

function useSimpleCrawlHandler(simpleCrawlConfig, crawlStateHandlers) {
  return useCallback(async () => {
    toast.info('Crawling en cours...');
    crawlStateHandlers.setProgress({ status: 'Crawling de la page...', current_page: 0, max_pages: 1 });
    return runStreamingCrawl('/documents/crawl-url', buildSimpleCrawlPayload(simpleCrawlConfig), crawlStateHandlers);
  }, [crawlStateHandlers, simpleCrawlConfig]);
}

function useBatchCrawlHandler(batchConfig, crawlStateHandlers) {
  return useCallback(async () => {
    const urls = parseUrlList(batchConfig.urlList);
    toast.info(`Crawling de ${urls.length} URLs en cours...`);
    crawlStateHandlers.setProgress({ status: `Démarrage du crawl de ${urls.length} URLs...`, url_index: 0, url_total: urls.length });
    return runStreamingCrawl('/documents/crawl-url-batch', buildBatchCrawlPayload(batchConfig), crawlStateHandlers);
  }, [batchConfig, crawlStateHandlers]);
}

function useCrawlSubmitHandler(crawlStateHandlers, crawlState, executionConfig, handleSimpleCrawl, handleBatchCrawl, runRecursiveCrawl) {
  return useCallback(async (event) => {
    event.preventDefault();

    if (executionConfig.isBatch) {
      const batchError = validateBatchUrls(executionConfig.urlList) || validateAuthConfig(crawlState);
      if (batchError) {
        toast.error(batchError);
        return;
      }
    } else {
      const validationError = validateCrawlUrl(executionConfig.url) || validateAuthConfig(crawlState);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    resetCrawlState(crawlStateHandlers);
    let crawlCompleted = false;

    try {
      if (executionConfig.isBatch) {
        crawlCompleted = await handleBatchCrawl();
      } else if (executionConfig.isRecursive) {
        crawlCompleted = await runRecursiveCrawl();
      } else {
        crawlCompleted = await handleSimpleCrawl();
      }
    } catch (error) {
      toast.error(getCrawlErrorMessage(error));
    } finally {
      finalizeCrawlState(crawlCompleted, crawlStateHandlers);
    }
  }, [crawlState, crawlStateHandlers, executionConfig, handleBatchCrawl, handleSimpleCrawl, runRecursiveCrawl]);
}

function useCrawlConfigs(crawlState, knowledgeBaseId, onSuccess) {
  const authFields = useMemo(
    () => ({
      authMode: crawlState.authMode,
      username: crawlState.username,
      password: crawlState.password,
      sessionCookies: crawlState.sessionCookies,
      googleEmail: crawlState.googleEmail,
      googlePassword: crawlState.googlePassword,
      rememberGoogleSession: crawlState.rememberGoogleSession,
      useSavedGoogleSession: crawlState.useSavedGoogleSession,
      forceRecrawl: crawlState.forceRecrawl,
    }),
    [crawlState.authMode, crawlState.username, crawlState.password,
     crawlState.sessionCookies, crawlState.googleEmail, crawlState.googlePassword,
     crawlState.rememberGoogleSession, crawlState.useSavedGoogleSession,
     crawlState.forceRecrawl]
  );

  const simpleCrawlConfig = useMemo(
    () => ({ knowledgeBaseId, url: crawlState.url, ...authFields, onSuccess }),
    [authFields, crawlState.url, knowledgeBaseId, onSuccess]
  );
  const recursiveCrawlConfig = useMemo(
    () => ({ knowledgeBaseId, url: crawlState.url, maxPages: crawlState.maxPages, urlPrefix: crawlState.urlPrefix, ...authFields }),
    [authFields, crawlState.maxPages, crawlState.url, crawlState.urlPrefix, knowledgeBaseId]
  );
  const batchConfig = useMemo(
    () => ({ knowledgeBaseId, urlList: crawlState.urlList, ...authFields, onSuccess }),
    [authFields, crawlState.urlList, knowledgeBaseId, onSuccess]
  );
  const executionConfig = useMemo(
    () => ({ isRecursive: crawlState.isRecursive, isBatch: crawlState.isBatch, url: crawlState.url, urlList: crawlState.urlList }),
    [crawlState.isBatch, crawlState.isRecursive, crawlState.url, crawlState.urlList]
  );

  return { simpleCrawlConfig, recursiveCrawlConfig, batchConfig, executionConfig };
}

export function useCrawlExecution({ knowledgeBaseId, onClose, onSuccess }) {
  const crawlState = useCrawlFormState();
  const crawlStateHandlers = useMemo(
    () => ({
      setCrawling: crawlState.setCrawling,
      setProgress: crawlState.setProgress,
      setCompletedResult: crawlState.setCompletedResult,
    }),
    [crawlState.setCompletedResult, crawlState.setCrawling, crawlState.setProgress]
  );

  const { simpleCrawlConfig, recursiveCrawlConfig, batchConfig, executionConfig } = useCrawlConfigs(crawlState, knowledgeBaseId, onSuccess);

  const handleClose = useCrawlCloseHandler(crawlState.completedResult, onClose, onSuccess);
  const handleSimpleCrawl = useSimpleCrawlHandler(simpleCrawlConfig, crawlStateHandlers);
  const handleBatchCrawl = useBatchCrawlHandler(batchConfig, crawlStateHandlers);
  const runRecursiveCrawl = useRecursiveCrawlStream(recursiveCrawlConfig, crawlStateHandlers);
  const handleCrawl = useCrawlSubmitHandler(
    crawlStateHandlers, crawlState, executionConfig,
    handleSimpleCrawl, handleBatchCrawl, runRecursiveCrawl
  );

  return {
    ...crawlState,
    handleClose,
    handleCrawl,
  };
}
